import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { WifiOff } from 'lucide-react';
import type { SelfUser, StandingAction } from '@spawnwire/shared';
import { Wordmark } from './components/Logo';
import { Button, Spinner, TooltipProvider } from './components/ui';
import { AuthPage } from './features/auth/AuthPage';
import { InvitePage } from './features/auth/InvitePage';
import { isDocsPath } from './features/developer/docsPath';
import { LegalPage, legalDocFor } from './features/legal/LegalPage';
import { AppShell } from './features/shell/AppShell';
import { BanScreen } from './features/standing/Standing';
import { StatusPage } from './features/status/StatusPage';
import { PublicSupport } from './features/support/PublicSupport';
import { standing, useStanding } from './features/standing/store';
import { ApiError, http, setBannedHandler, setUnauthorizedHandler } from './lib/api';
import { connectGateway, disconnectGateway, startHousekeeping } from './lib/gateway';
import { themeCache } from './lib/local';
import { native, startNative } from './lib/native';
import { setNavigator } from './lib/nav';
import { getAuthToken, isNative } from './lib/platform';
import { wireVoice } from './lib/voice';
import { actions, useStore } from './store/store';

const DocsPage = lazy(() => import('./features/developer/DocsPage'));
const AddBotPage = lazy(() => import('./features/developer/AddBotPage'));

function PageSpinner() {
  return (
    <div className="flex h-full items-center justify-center bg-rail">
      <Spinner className="size-8" />
    </div>
  );
}

/** `forceDark` is for the public status page, which keeps Spawnwire's dark look whatever the visitor's setting. */
function useAppearance(forceDark: boolean): void {
  const settings = useStore((s) => s.me?.settings);
  useEffect(() => {
    const root = document.documentElement;
    const theme = forceDark ? 'dark' : (settings?.theme ?? (themeCache.get() as 'system' | 'light' | 'dark' | null) ?? 'system');
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const apply = () => {
      const resolved = theme === 'system' ? (media.matches ? 'light' : 'dark') : theme;
      root.dataset.theme = resolved;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolved === 'light' ? '#f0f2f5' : '#14171c');
    };
    apply();
    if (settings && !forceDark) themeCache.set(theme);
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [settings, forceDark]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = `${settings?.fontScale ?? 100}%`;
    root.dataset.reducedMotion = String(settings?.reducedMotion ?? false);
    root.dataset.contrast = settings?.highContrast ? 'high' : 'normal';
    root.dataset.underlineLinks = String(settings?.underlineLinks ?? false);
    root.dataset.density = settings?.density ?? 'cozy';
  }, [settings]);
}

/**
 * The server couldn't be reached while checking the sign-in (offline, or a restart in progress). The sign-in is
 * kept; this retries when the connection comes back, and every few seconds meanwhile.
 */
function Unreachable({ onRetry }: { onRetry: () => void }) {
  useEffect(() => {
    window.addEventListener('online', onRetry);
    const timer = setInterval(onRetry, 10_000);
    return () => {
      window.removeEventListener('online', onRetry);
      clearInterval(timer);
    };
  }, [onRetry]);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 bg-rail p-6 text-center">
      <Wordmark />
      <WifiOff className="size-10 text-muted" aria-hidden />
      <div className="max-w-xs space-y-1">
        <p className="font-semibold text-fg">Can't reach Spawnwire</p>
        <p className="text-sm text-muted">Check your connection. You're still signed in, and Spawnwire will reconnect as soon as it can.</p>
      </div>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  );
}

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useStore((s) => s.me);
  const ban = useStanding((s) => s.ban);
  const [booting, setBooting] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const onStatusPage = location.pathname === '/status' || location.pathname === '/status/';
  const legalDoc = legalDocFor(location.pathname);
  useAppearance(onStatusPage);

  useEffect(() => {
    setNavigator((to) => navigate(to));
  }, [navigate]);

  const checkSession = useCallback(() => {
    http
      .get<{ user: SelfUser; ban: StandingAction | null }>('/auth/session')
      .then(({ user, ban }) => {
        if (ban) standing.banned(ban);
        setUnreachable(false);
        actions.setMe(user);
        setBooting(false);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 0 || err.status >= 500)) {
          setUnreachable(true);
          return;
        }
        // Signed out, or the session expired or was revoked.
        if (err instanceof ApiError && err.status === 401) void native?.signedOut();
        setUnreachable(false);
        actions.setMe(null);
        setBooting(false);
      });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      void native?.signedOut();
      disconnectGateway();
      actions.reset();
      standing.clear();
    });
    setBannedHandler((ban) => {
      disconnectGateway();
      standing.banned(ban);
    });
    void startNative().then(() => {
      // The app knows without asking when nobody is signed in on it, even offline.
      if (isNative && !getAuthToken()) {
        actions.setMe(null);
        setBooting(false);
        return;
      }
      checkSession();
    });
  }, [checkSession]);

  const meId = me?.id;
  const banned = !!ban;
  useEffect(() => {
    if (!meId || banned) return;
    connectGateway();
    wireVoice();
    startHousekeeping();
  }, [meId, banned]);

  // Public: no sign-in, and it must not wait on (or be blocked by) the session check.
  if (onStatusPage) {
    return (
      <TooltipProvider delayDuration={100}>
        <StatusPage />
      </TooltipProvider>
    );
  }
  // The rules, terms and privacy policy are public too, for everyone including banned accounts.
  if (legalDoc) return <LegalPage doc={legalDoc} />;
  // So is the bot documentation (loaded on demand: it carries the whole guide).
  if (isDocsPath(location.pathname)) {
    return (
      <TooltipProvider delayDuration={300}>
        <Suspense fallback={<PageSpinner />}>
          <DocsPage />
        </Suspense>
      </TooltipProvider>
    );
  }

  if (booting && unreachable) return <Unreachable onRetry={checkSession} />;
  if (booting) {
    return (
      <div className="flex h-full items-center justify-center bg-main">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (me && ban) {
    return (
      <TooltipProvider delayDuration={300}>
        <BanScreen ban={ban} />
      </TooltipProvider>
    );
  }

  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <TooltipProvider delayDuration={300}>
      <Routes>
        <Route path="/login" element={me ? <Navigate to={new URLSearchParams(location.search).get('next') ?? '/app/@me'} replace /> : <AuthPage mode="login" />} />
        <Route path="/register" element={me ? <Navigate to="/app/@me" replace /> : <AuthPage mode="register" />} />
        <Route path="/invite/:code" element={me ? <InvitePage /> : <Navigate to={`/login?next=${next}`} replace />} />
        <Route
          path="/bots/:botId/add"
          element={
            me ? (
              <Suspense fallback={<PageSpinner />}>
                <AddBotPage />
              </Suspense>
            ) : (
              <Navigate to={`/login?next=${next}`} replace />
            )
          }
        />
        {/* Open to people who can't sign in, so they can still reach support. */}
        <Route path="/support/*" element={<PublicSupport signedIn={!!me} />} />
        <Route path="/app/*" element={me ? <AppShell /> : <Navigate to={`/login?next=${next}`} replace />} />
        <Route path="*" element={<Navigate to={me ? '/app/@me' : '/login'} replace />} />
      </Routes>
    </TooltipProvider>
  );
}
